import { useEffect, useMemo } from 'react';
import { Button, Field } from '@ecobox/ui';

/** Small previews of the pictures taken, until the command is sent. */
export function usePreviews(files: readonly File[]): string[] {
  const urls = useMemo(
    () =>
      files.map((f) => (typeof URL.createObjectURL === 'function' ? URL.createObjectURL(f) : '')),
    [files],
  );
  useEffect(
    () => () => {
      for (const u of urls) {
        if (u && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(u);
      }
    },
    [urls],
  );
  return urls;
}

/**
 * Take pictures with the camera (or pick them). Each one is shrunk and saved on the phone when
 * the command is made, and uploads when there is signal (02 §2.2), so a missing connection never
 * stops a delivery.
 */
export function PhotoField({
  label,
  files,
  onChange,
  max = 5,
  hint,
}: {
  label: string;
  files: readonly File[];
  onChange: (files: File[]) => void;
  max?: number;
  hint?: string;
}) {
  const previews = usePreviews(files);
  return (
    <Field
      label={label}
      hint={hint ?? `${files.length} of ${max} photos. They upload when you have signal.`}
    >
      {(p) => (
        <div className="ops-stack">
          {files.length < max ? (
            <input
              id={p.id}
              aria-describedby={p['aria-describedby']}
              className="ops-file"
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => {
                const picked = Array.from(e.target.files ?? []);
                if (picked.length > 0) onChange([...files, ...picked].slice(0, max));
                e.target.value = '';
              }}
            />
          ) : null}
          {files.length > 0 ? (
            <div className="ops-thumbs">
              {files.map((f, i) => (
                <div key={`${f.name}-${i}`} className="ops-stack">
                  {previews[i] ? (
                    <img className="ops-thumb" src={previews[i]} alt={`${label} ${i + 1}`} />
                  ) : null}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onChange(files.filter((_, j) => j !== i))}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      )}
    </Field>
  );
}
